"use client";

import cmsContentClassName from "@casino/shared/cms/cmsContentClassName";
import { useGamePageData } from "@casino/ui/Games/hooks";
import { Button, cn, TypographyH4 } from "@workspace/frontend-ui";
import type { FC, PropsWithChildren } from "react";
import * as React from "react";

type Props = PropsWithChildren<{
  title?: string;
  isCollapsedByDefault?: boolean;
  scrollTargetId?: string;
}>;

const CollapsibleSeoText: FC<Props> = ({
  children,
  title,
  isCollapsedByDefault,
  scrollTargetId,
}) => {
  const [isCollapsed, setCollapsed] = React.useState(!!isCollapsedByDefault);
  const { t } = useGamePageData();

  const handleToggle = () => {
    const willCollapse = !isCollapsed;
    setCollapsed(willCollapse);

    if (willCollapse && scrollTargetId) {
      document
        .getElementById(scrollTargetId)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <section className="mt-6">
      {title && <TypographyH4 isBold>{title}</TypographyH4>}
      {/* Collapsing is a max-height, never a conditional render — the article must stay in the DOM for SEO. */}
      <div
        id="game-seo-text"
        data-testid="game-seo-text"
        className={cn(
          "mt-4",
          ...cmsContentClassName,
          isCollapsed &&
            "relative max-h-52 overflow-hidden after:absolute after:bottom-0 after:left-0 after:h-16 after:w-full after:bg-gradient-to-t after:from-background",
        )}
      >
        {children}
      </div>
      <div className="flex justify-center pt-2">
        <Button
          variant="link"
          onClick={handleToggle}
          aria-expanded={!isCollapsed}
          aria-controls="game-seo-text"
        >
          {isCollapsed
            ? t("game.seo.showmore", "Show more")
            : t("game.seo.showless", "Show less")}
        </Button>
      </div>
    </section>
  );
};

export default CollapsibleSeoText;
